import { Key, useCallback, useMemo } from 'react'
import clsx from 'clsx'
import { Checkbox, ColumnsType, Table } from 'components/common'
import {
  ProductWholeBookingQuery,
  VariantAvailable,
} from 'lib/graphql/storeadmin'
import { date } from 'lib/str-formatter'

// VariantAvailable のままだと型定義エラーになるので Omit して継承
export interface CustomVariantAvailable extends Omit<VariantAvailable, 'id'> {}

export interface BulkTableProps {
  data?: ProductWholeBookingQuery
  selectedDates: Key[]
  setSelectedDates: (keys: Key[]) => void
  selectedVariants: CustomVariantAvailable[]
  setSelectedVariants: React.Dispatch<
    React.SetStateAction<CustomVariantAvailable[]>
  >
}

export const BulkTable: React.FC<BulkTableProps> = ({
  data,
  selectedDates,
  setSelectedDates,
  setSelectedVariants,
}): JSX.Element => {
  const onCheckboxes = useCallback(
    (variant: CustomVariantAvailable) => {
      setSelectedVariants((prevSelectedVariants) => {
        if (prevSelectedVariants.indexOf(variant) === -1) {
          return [...prevSelectedVariants, variant]
        } else {
          return prevSelectedVariants.filter((v) => v !== variant)
        }
      })
    },
    [setSelectedVariants]
  )

  // ここからヘッダー部分
  const columns = useMemo<ColumnsType>(() => {
    const column: ColumnsType = [
      {
        title: '日付',
        dataIndex: 'date',
        key: 'date',
        fixed: 'left',
        width: 100,
        render: (value) => date(value),
      },
    ]
    const { bookingVariants } =
      data?.productWholeBooking?.edges[0]?.wholeBooking || {}
    bookingVariants?.edges?.forEach(({ variant }) => {
      column.push({
        title: (
          <Checkbox onChange={() => onCheckboxes(variant)}>
            {variant.variantName}
          </Checkbox>
        ),
        dataIndex: variant.variantId,
        key: variant.variantId,
        width: 80,
        render: (bool) => (bool ? '' : '✔'),
      })
    })
    return column
  }, [data, onCheckboxes])
  // ここまでヘッダー部分

  const dataSource = useMemo(() => {
    return data?.productWholeBooking?.edges?.reduce(
      (result, { wholeBooking }) => {
        const record = {
          key: wholeBooking?.bookingDate,
          date: wholeBooking?.bookingDate,
        }
        wholeBooking?.bookingVariants?.edges?.forEach(({ variant }) => {
          record[variant?.variantId] = variant?.isAvailable
        })
        result.push(record)
        return result
      },
      []
    )
  }, [data])

  const rowSelection = {
    selectedRowKeys: selectedDates,
    onChange: (selectedKeys: Key[]) => setSelectedDates(selectedKeys),
  }

  return (
    <Table
      bordered
      sticky
      rowClassName={clsx('cursor-pointer')}
      rowSelection={rowSelection}
      dataSource={dataSource}
      columns={columns}
      pagination={false}
      scroll={{ x: 2000 }}
    />
  )
}
